/**
 * Add-to-calendar utilities for tournament events.
 * Builds URLs and .ics content for Google, Outlook, Yahoo, and Apple/iCal.
 */

import { TournamentDTO } from '../types/tournaments'

/** Format ISO date to Google Calendar format: YYYYMMDDTHHmmssZ (UTC) */
function toGoogleDate(iso: string): string {
    const d = new Date(iso)
    const y = d.getUTCFullYear()
    const m = String(d.getUTCMonth() + 1).padStart(2, '0')
    const day = String(d.getUTCDate()).padStart(2, '0')
    const h = String(d.getUTCHours()).padStart(2, '0')
    const min = String(d.getUTCMinutes()).padStart(2, '0')
    const s = String(d.getUTCSeconds()).padStart(2, '0')
    return `${y}${m}${day}T${h}${min}${s}Z`
}

function formatLocation(t: TournamentDTO): string {
    const a = t.location.address
    return [
        t.location.venueName,
        a.street,
        [a.postcode, a.city].filter(Boolean).join(' '),
        a.country,
    ]
        .filter(Boolean)
        .join(', ')
}

/** Google Calendar "Add event" URL */
export function getGoogleCalendarUrl(tournament: TournamentDTO): string {
    const base = 'https://www.google.com/calendar/render'
    const params = new URLSearchParams({
        action: 'TEMPLATE',
        text: tournament.info.title,
        dates: `${toGoogleDate(tournament.info.startDate)}/${toGoogleDate(tournament.info.endDate)}`,
        details: tournament.info.description,
        location: formatLocation(tournament),
    })
    return `${base}?${params.toString()}`
}

/** ISO date to UTC ISO string for Outlook (e.g. 2025-04-25T12:00:00Z) */
function toUtcIso(iso: string): string {
    const d = new Date(iso)
    return d.toISOString()
}

/** Outlook.com / Office 365 "New event" URL */
export function getOutlookCalendarUrl(tournament: TournamentDTO): string {
    const base = 'https://outlook.live.com/calendar/deeplink/compose'
    const params = new URLSearchParams({
        path: '/calendar/action/compose',
        rru: 'addevent',
        startdt: toUtcIso(tournament.info.startDate),
        enddt: toUtcIso(tournament.info.endDate),
        subject: tournament.info.title,
        body: tournament.info.description,
        location: formatLocation(tournament),
    })
    return `${base}?${params.toString()}`
}

/** Yahoo Calendar add event URL (v=60 format) */
export function getYahooCalendarUrl(tournament: TournamentDTO): string {
    const base = 'https://calendar.yahoo.com/'
    const params = new URLSearchParams({
        v: '60',
        title: tournament.info.title,
        st: toGoogleDate(tournament.info.startDate),
        et: toGoogleDate(tournament.info.endDate),
        desc: tournament.info.description,
        in_loc: formatLocation(tournament),
    })
    return `${base}?${params.toString()}`
}

/** Escape text for .ics (fold long lines and escape special chars) */
function escapeIcsText(s: string): string {
    return s.replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;')
}

/** Generate .ics file content for download (Apple Calendar, iCal, etc.) */
export function getIcsContent(tournament: TournamentDTO): string {
    const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Dailyvents//Tournament//EN',
        'BEGIN:VEVENT',
        `DTSTART:${toGoogleDate(tournament.info.startDate)}`,
        `DTEND:${toGoogleDate(tournament.info.endDate)}`,
        `SUMMARY:${escapeIcsText(tournament.info.title)}`,
        `DESCRIPTION:${escapeIcsText(tournament.info.description)}`,
        `LOCATION:${escapeIcsText(formatLocation(tournament))}`,
        'END:VEVENT',
        'END:VCALENDAR',
    ]
    return lines.join('\r\n')
}

/** Trigger download of .ics file */
export function downloadIcs(tournament: TournamentDTO): void {
    const ics = getIcsContent(tournament)
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${tournament.info.title.replace(/[^a-z0-9]/gi, '-')}.ics`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
}

export interface CalendarOption {
    key: string
    label: string
    onClick: () => void
}

/** Options for "Add to Calendar" dropdown (label + action) */
export function getCalendarOptions(
    tournament: TournamentDTO,
    t: (key: string) => string
): CalendarOption[] {
    return [
        {
            key: 'google',
            label: 'Google Calendar',
            onClick: () =>
                window.open(getGoogleCalendarUrl(tournament), '_blank'),
        },
        {
            key: 'outlook',
            label: 'Outlook',
            onClick: () =>
                window.open(getOutlookCalendarUrl(tournament), '_blank'),
        },
        {
            key: 'yahoo',
            label: 'Yahoo Calendar',
            onClick: () =>
                window.open(getYahooCalendarUrl(tournament), '_blank'),
        },
        {
            key: 'apple',
            label: t('tournaments.cups.addToCalendarApple'),
            onClick: () => downloadIcs(tournament),
        },
    ]
}
